import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AdminOnboardingUsersTable.module.css';
import config, { logger } from '../config';
import {
  fetchAdminOnboardingAIChatData,
  deleteAdminUser,
  fetchAdminOnboardingUsers,
  updateV2OnboardingAiResult,
  patchV2OnboardingAiDisplayMetrics,
  generateOnboardingCharts,
  getToken,
} from '../utils/api';
import {
  normalizeOnboardingProgressKeys,
  fetchSimulationOutputForCharts,
  calculatedFinancialDataToV2OnboardingAiResult,
  onboardingProgressFromV2OnboardingAiResult,
  normalizeV2OnboardingAiResult,
  resolveOnboardingProgressFromAdminData,
} from '../utils/onboardingUtils';
import {
  extractChatQuestionsAndAnswers,
  createCategoriesFromChatQuestions,
} from '../utils/helper';
import { processCategoryQuestions } from './UserProfile/orderQuestions';
import { buildCategoriesFromFinancialProfile } from './UserProfile/buildFinancialProfileCategories';
import { useContentCatalog } from '../utils/contentCatalog';
import { sortCategoriesById } from './AdminOnboardingUsersTable/utils';
import {
  AdminTableLoadingState,
  buildChartReportHtml,
} from './AdminOnboardingUsersTable/helpers';
import UsersTable from './AdminOnboardingUsersTable/UsersTable';
import {
  UserDetailModal,
  DashboardDataModal,
  ChartDetailModal,
  DeleteUserConfirmModal,
} from './AdminOnboardingUsersTable/modals';

const EMPTY_DETAIL_CONTEXT = {
  categories: [],
  questionsMap: {},
  mergedOnboarding: {},
};

const AdminOnboardingUsersTable = () => {
  const navigate = useNavigate();
  const { catalog } = useContentCatalog();
  const profileSectionTitles = useMemo(
    () => (catalog?.financial_profile_sections || []).map((item) => item.title),
    [catalog],
  );

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedUser, setSelectedUser] = useState(null);
  const [dashboardUser, setDashboardUser] = useState(null);
  const [chartDetailUser, setChartDetailUser] = useState(null);
  const [pendingDeleteUser, setPendingDeleteUser] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [dashboardOnboardingProgress, setDashboardOnboardingProgress] = useState(null);
  const [dashboardOnboardingAiCheckupAndActionPlan, setDashboardOnboardingAiCheckupAndActionPlan] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const [detailChatData, setDetailChatData] = useState(null);
  const [detailV2ResultFull, setDetailV2ResultFull] = useState(null);
  const [detailChatLoading, setDetailChatLoading] = useState(false);

  const [isChartLoading, setIsChartLoading] = useState({});
  const [chartDetailHtml, setChartDetailHtml] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteDialogError, setDeleteDialogError] = useState('');

  const isSuperadmin = useMemo(() => {
    const role = localStorage.getItem(config.STORAGE_KEYS.ADMIN_ROLE);
    return String(role ?? '').trim().toLowerCase() === 'superadmin';
  }, []);

  const fetchOnboardingUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const token = getToken();
      if (!token) {
        localStorage.clear();
        navigate('/');
        return;
      }

      const result = await fetchAdminOnboardingUsers(navigate);
      if (result.ok) {
        setUsers(result.users || []);
      } else {
        setError(result.message || 'Failed to fetch onboarding users');
      }
    } catch (fetchError) {
      logger.error('Error fetching onboarding users:', fetchError);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchOnboardingUsers();
  }, [fetchOnboardingUsers]);

  const closePendingDeleteDialog = useCallback(() => {
    if (deleteSubmitting) return;
    setPendingDeleteUser(null);
    setDeleteDialogError('');
  }, [deleteSubmitting]);

  const closeModal = useCallback(() => {
    setSelectedUser(null);
    setDetailChatData(null);
    setDetailV2ResultFull(null);
    setDetailChatLoading(false);
  }, []);

  const closeDashboardModal = useCallback(() => {
    setDashboardUser(null);
    setDashboardOnboardingProgress(null);
    setDashboardOnboardingAiCheckupAndActionPlan(null);
    setDashboardLoading(false);
  }, []);

  const handleDisplayMetricsReady = useCallback(async ({ runwayDisplay, longevityDisplay }) => {
    if (!dashboardUser?.username) return;
    await patchV2OnboardingAiDisplayMetrics(
      { runwayDisplay, longevityDisplay, username: dashboardUser.username },
      navigate,
      { isAdmin: true }
    );
  }, [dashboardUser?.username, navigate]);

  const closeChartDetailModal = useCallback(() => {
    setChartDetailUser(null);
    setChartDetailHtml(null);
  }, []);

  const anyModalOpen = Boolean(
    selectedUser || dashboardUser || chartDetailUser || pendingDeleteUser
  );

  useEffect(() => {
    if (!anyModalOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (pendingDeleteUser) {
        closePendingDeleteDialog();
        return;
      }
      if (selectedUser) closeModal();
      else if (dashboardUser) closeDashboardModal();
      else if (chartDetailUser) closeChartDetailModal();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [
    anyModalOpen,
    selectedUser,
    dashboardUser,
    chartDetailUser,
    pendingDeleteUser,
    closePendingDeleteDialog,
    closeModal,
    closeDashboardModal,
    closeChartDetailModal,
  ]);

  useEffect(() => {
    if (!anyModalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [anyModalOpen]);

  const handleDetailClick = useCallback(async (user) => {
    setSelectedUser(user);
    setDetailChatData(null);
    setDetailV2ResultFull(null);
    setDetailChatLoading(true);

    const result = await fetchAdminOnboardingAIChatData(user.username);
    const chat = result?.chatData;
    setDetailChatData(Array.isArray(chat) ? chat : []);
    const v2 = result?.onboardingAiResult;
    setDetailV2ResultFull(v2 && typeof v2 === 'object' ? normalizeV2OnboardingAiResult(v2) : null);
    setDetailChatLoading(false);
  }, []);

  const handleDashboardClick = useCallback(async (user) => {
    setDashboardUser(user);
    setDashboardOnboardingProgress(null);
    setDashboardOnboardingAiCheckupAndActionPlan(null);
    setDashboardLoading(true);

    try {
      const result = await fetchAdminOnboardingAIChatData(user.username);

      if (!result) {
        setDashboardOnboardingProgress(normalizeOnboardingProgressKeys(user.onboarding || {}));
        return;
      }

      if (result.checkupAndActionPlan != null) {
        setDashboardOnboardingAiCheckupAndActionPlan(result.checkupAndActionPlan);
      }

      const { progress, financialDataResult } = await resolveOnboardingProgressFromAdminData({
        result,
        fallbackOnboarding: user.onboarding || {},
        useAdminEndpoint: true,
      });
      setDashboardOnboardingProgress(progress);

      if (financialDataResult) {
        const token = getToken();
        if (token) {
          const saveResult = await updateV2OnboardingAiResult(
            {
              token,
              username: user.username,
              v2OnboardingAiResult: calculatedFinancialDataToV2OnboardingAiResult(financialDataResult),
            },
            navigate,
            { isAdmin: true }
          );
          if (!saveResult.ok) {
            logger.warn('Failed to save financial data:', saveResult.message || 'Unknown error');
          }
        }
      }
    } catch (dashboardError) {
      logger.error('Error processing dashboard data:', dashboardError);
      setDashboardOnboardingProgress(normalizeOnboardingProgressKeys(user.onboarding || {}));
    } finally {
      setDashboardLoading(false);
    }
  }, [navigate]);

  const handleDeleteUserClick = useCallback((user) => {
    const username = String(user?.username ?? '').trim();
    if (!username || !isSuperadmin || deleteSubmitting) return;
    setPendingDeleteUser(user);
    setDeleteDialogError('');
  }, [deleteSubmitting, isSuperadmin]);

  const handleConfirmDeleteUser = useCallback(async () => {
    const user = pendingDeleteUser;
    if (!user || deleteSubmitting) return;
    const username = String(user.username ?? '').trim();
    if (!username) return;

    setDeleteSubmitting(true);
    setDeleteDialogError('');
    try {
      const result = await deleteAdminUser({ username }, navigate);
      if (!result.ok) {
        setDeleteDialogError(result.message || 'Failed to delete user.');
        return;
      }
      setUsers((prev) => prev.filter((item) => item.username !== username));
      setPendingDeleteUser(null);
    } catch (deleteError) {
      logger.error('Error deleting onboarding user:', deleteError);
      setDeleteDialogError('Network error. Please try again.');
    } finally {
      setDeleteSubmitting(false);
    }
  }, [deleteSubmitting, navigate, pendingDeleteUser]);

  const handleChartDetailClick = useCallback(async (user) => {
    const username = user.username;
    setIsChartLoading((prev) => ({ ...prev, [username]: true }));

    try {
      const result = await fetchAdminOnboardingAIChatData(username);
      const { progress: onboardingProgress } = await resolveOnboardingProgressFromAdminData({
        result,
        fallbackOnboarding: user.onboarding || {},
        useAdminEndpoint: true,
      });

      const { output, requestBody } = await fetchSimulationOutputForCharts(onboardingProgress);
      if (!output) {
        logger.warn('Chart Detail: No simulation output available');
        return;
      }

      const chartsResult = await generateOnboardingCharts(
        {
          output,
          input: requestBody,
          case_id: 'Report',
          user_name: user.name || '',
          include_configuration: false,
        },
        { isAdmin: true }
      );

      if (chartsResult.ok && chartsResult.fragments) {
        setChartDetailUser(user);
        setChartDetailHtml(buildChartReportHtml(chartsResult.fragments, user.name));
      } else {
        logger.error('ONBOARDING_AI_CHART_GENERATOR error:', chartsResult.message || 'Unknown error');
      }
    } catch (chartsErr) {
      if (chartsErr?.name !== 'AbortError') {
        logger.error('ONBOARDING_AI_CHART_GENERATOR request failed:', chartsErr);
      }
    } finally {
      setIsChartLoading((prev) => {
        const next = { ...prev };
        delete next[username];
        return next;
      });
    }
  }, []);

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const filteredAndSortedUsers = useMemo(() => {
    const searchLower = searchTerm.trim().toLowerCase();
    const filtered = searchLower
      ? users.filter((user) =>
          user.name?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower)
        )
      : users;

    if (!sortConfig.key) return filtered;

    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const aValue = String(a[sortConfig.key] ?? '').toLowerCase();
      const bValue = String(b[sortConfig.key] ?? '').toLowerCase();
      if (aValue < bValue) return -direction;
      if (aValue > bValue) return direction;
      return 0;
    });
  }, [users, searchTerm, sortConfig]);

  const adminDetailViewContext = useMemo(() => {
    if (!selectedUser) return EMPTY_DETAIL_CONTEXT;

    const chatArraySource = Array.isArray(detailChatData) ? detailChatData : [];
    const extracted =
      chatArraySource.length > 0
        ? extractChatQuestionsAndAnswers(chatArraySource)
        : { answersMap: {}, questionsMap: {} };

    const v2Norm = normalizeV2OnboardingAiResult(detailV2ResultFull) ?? detailV2ResultFull;
    const fp = onboardingProgressFromV2OnboardingAiResult(v2Norm);
    const fpObj = fp && typeof fp === 'object' && !Array.isArray(fp) ? fp : null;
    const fpKeyCount = fpObj ? Object.keys(fpObj).length : 0;

    let categories = [];
    if (fpKeyCount > 0) {
      categories = buildCategoriesFromFinancialProfile(fpObj, null, profileSectionTitles);
    } else if (Object.keys(extracted.questionsMap).length > 0) {
      categories = createCategoriesFromChatQuestions(extracted.questionsMap);
    }

    return {
      categories,
      questionsMap: extracted.questionsMap,
      mergedOnboarding: normalizeOnboardingProgressKeys({
        ...extracted.answersMap,
        ...(fpObj || {}),
      }),
    };
  }, [selectedUser, detailChatData, detailV2ResultFull, profileSectionTitles]);

  const adminDetailStructuredCategories = useMemo(() => {
    if (!selectedUser) return [];
    return sortCategoriesById((adminDetailViewContext.categories || []).map(processCategoryQuestions));
  }, [selectedUser, adminDetailViewContext.categories]);

  if (loading) {
    return (
      <div className={styles['loading-shell']}>
        <AdminTableLoadingState message="Loading onboarding users..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['error-container']}>
        <div className={styles['error-message']}>
          <h3>Error</h3>
          <p>{error}</p>
          <button type="button" onClick={fetchOnboardingUsers} className={styles['retry-button']}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['table-container']}>
      {selectedUser ? (
        <UserDetailModal
          user={selectedUser}
          loading={detailChatLoading}
          structuredCategories={adminDetailStructuredCategories}
          viewContext={adminDetailViewContext}
          onClose={closeModal}
        />
      ) : null}
      {dashboardUser ? (
        <DashboardDataModal
          user={dashboardUser}
          loading={dashboardLoading}
          onboardingProgress={dashboardOnboardingProgress}
          checkupAndActionPlan={dashboardOnboardingAiCheckupAndActionPlan}
          onDisplayMetricsReady={handleDisplayMetricsReady}
          onClose={closeDashboardModal}
        />
      ) : null}
      {chartDetailUser ? (
        <ChartDetailModal
          user={chartDetailUser}
          html={chartDetailHtml}
          onClose={closeChartDetailModal}
        />
      ) : null}
      {pendingDeleteUser ? (
        <DeleteUserConfirmModal
          user={pendingDeleteUser}
          submitting={deleteSubmitting}
          error={deleteDialogError}
          onConfirm={handleConfirmDeleteUser}
          onClose={closePendingDeleteDialog}
        />
      ) : null}
      <UsersTable
        users={users}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onClearSearch={handleClearSearch}
        filteredAndSortedUsers={filteredAndSortedUsers}
        sortConfig={sortConfig}
        onSort={handleSort}
        isChartLoading={isChartLoading}
        onDashboardClick={handleDashboardClick}
        onChartDetailClick={handleChartDetailClick}
        onDetailClick={handleDetailClick}
        onDeleteUserClick={handleDeleteUserClick}
        isSuperadmin={isSuperadmin}
        deleteSubmittingUsername={
          deleteSubmitting && pendingDeleteUser?.username ? pendingDeleteUser.username : null
        }
      />
    </div>
  );
};

export default AdminOnboardingUsersTable;
